import { defineStore } from 'pinia';

import creatorsRepository from '@/api/creatorsRepository';
import threadRepository from '@/api/threadRepository';
import usersRepository from '@/api/usersRepository';
import type { Creator } from '@/types/creator';
import type {
  GetThreadsParams,
  HomeTabs,
  ReplyThreadParams,
  SelectedSearchParam,
  SelectValue,
  SingleMessage,
  ThreadRow
} from '@/types/thread';
import type { User } from '@/types/user';
import { i18nConfig } from '@/utils/i18n';
import { errorHandler } from '@/utils/utils';

const t = i18nConfig.global.t;

interface State {
  threads: ThreadRow[] | [];
  messages: SingleMessage[] | [];
  subject: string | null
  starred: boolean | null
  trashed: boolean | null
  messageCounter: number
  loading: boolean
  offset: number
  maxResults: number
  pageToken: string | null
  searchUsers: User[] | []
  searchCreators: Creator[] | []
  searchParams: SelectedSearchParam | null
  selectedTab: HomeTabs
  selectedMode: SelectValue
  supportedVariables: string[]
  receivingCreator: Creator | null
}

export const useThreadStore = defineStore('threadStore', {
  state: (): State => {
    return {
      threads: [],
      messages: [],
      subject: null,
      starred: null,
      trashed: null,
      messageCounter: 0,
      loading: true,
      offset: 0,
      maxResults: 25,
      pageToken: null,
      searchUsers: [],
      searchCreators: [],
      searchParams: null,
      selectedTab: 'all',
      selectedMode: { label: t('home.modes.personal'), value: 'personal' },
      supportedVariables: [],
      receivingCreator: null
    };
  },

  getters: {
    prepareParams(): GetThreadsParams {
      return {
        receivingCreatorUuids: this.searchParams?.creator ? [this.searchParams?.creator.uuid] : [],
        outgoingSenderUuids: this.searchParams?.sender ? [this.searchParams?.sender.uuid] : [],
        searchPhrase: this.searchParams?.searchPhrase,
        allOfCompany: this.selectedMode.value !== 'personal',
        sortOrder: 'desc',
        pageToken: this.pageToken,
        maxResults: this.maxResults
      };
    }
  },

  actions: {
    resetState() {
      const keepValues = {
        selectedTab: this.$state.selectedTab,
        selectedMode: this.$state.selectedMode
      };

      this.$reset();

      Object.assign(this, keepValues);
    },

    clearState() {
      this.$state.offset = 0;
      this.$state.pageToken = null;
      this.$state.threads = [];
    },

    setOffset() {
      this.$state.offset = this.$state.threads.length;
    },

    setSearchParams(params: SelectedSearchParam) {
      this.$state.searchParams = params;
    },

    setSelectedTab(tab: HomeTabs) {
      this.$state.selectedTab = tab;
    },

    async deleteThread() {
      try {
        await threadRepository.deleteThread();
      } catch (error) {
        errorHandler(error);
      }
    },

    async getCreatorSearchSuggestions(search: string) {
      try {
        if (!search) {
          this.$state.searchCreators = [];

          return;
        }

        const { data } = await creatorsRepository.search({ searchPhrase: search });
        this.$state.searchCreators = data.filter((item: Creator, i: number) => i < 4);
      } catch (error) {
        errorHandler(error);
      }
    },

    async getSearchSuggestions(searchPhrase: string) {
      try {
        if (!searchPhrase) {
          this.$state.searchCreators = [];
          this.$state.searchUsers = [];

          return;
        }

        const values = await Promise.all([
          creatorsRepository.index(searchPhrase),
          usersRepository.index(searchPhrase)
        ]);

        const [creatorsResponse, usersResponse] = values;
        const creators = creatorsResponse.data;
        const users = usersResponse.data;

        this.$state.searchCreators = creators.filter((item: Creator, i: number) => i < 4);
        this.$state.searchUsers = users.filter((item: User, i: number) => i < 4);
      } catch (error) {
        errorHandler(error);
      }
    },

    async getThreadList() {
      if (!this.$state.pageToken && this.$state.offset > 0) return;
      try {
        this.$state.loading = true;

        const { data, metadata } = await threadRepository.getThreadsList(this.prepareParams, this.$state.selectedTab);
        this.$state.pageToken = metadata.nextPageToken;

        this.$state.threads = [...this.$state.threads, ...data];
      } catch(error) {
        errorHandler(error);
      } finally {
        this.setOffset();
        this.$state.loading = false;
      }
    },

    async getMessagesList() {
      try {
        const { data } = await threadRepository.getMessagesList();
        this.$state.messages = data.mailboxEmails.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        this.$state.subject = data.subject;
        this.$state.starred = data.starred;
        this.$state.trashed = data.trashed;
        this.$state.messageCounter = data.mailboxEmailsCount;
        this.$state.receivingCreator = data.receivingCreator;

        return data;
      } catch (error) {
        errorHandler(error);
      }
    },

    async postReplyThread(paramsData: ReplyThreadParams) {
      try {
        if (!paramsData.uuid) return;

        const params = {
          ...paramsData,
          attachments: paramsData.attachments.map(attachment => {
            return attachment?._copy ? {
              uuid: attachment.uuid,
              _copy: true
            } : attachment;
          }),
          to: paramsData.to.map(item => {
            return typeof item === 'string' ? {
              email: item
            } : item;
          }),
          cc: paramsData.cc.map(item => {
            return typeof item === 'string' ? {
              email: item
            } : item;
          })
        };

        const { data } = await threadRepository.postReplyThread(params);

        return data;
      } catch (error) {
        errorHandler(error);
      }
    },

    async getSupportedVariables() {
      try {
        const { data } = await threadRepository.getSupportedVariables();

        this.$state.supportedVariables = data;
      } catch (error) {
        errorHandler(error);
      }
    }
  }
});
